import React from 'react';
import type { CandidateOpportunityOnboarding } from '@axiom/validation';
import {
  DateUtil,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  ReadonlyTextarea,
  SmallHeader,
  useBreakpoint,
  Visible,
  Well,
} from '@axiom/ui';
import type { UseOnboardingTextReturn } from '@axiom/ui';

import Satchel from '../../public/svg/satchel.svg';
import type { Nullable } from '../../types/utility.types';

import { EngagementDetailsFirstDayContacts } from './EngagementDetailsFirstDayContacts';

export type OnboardingFirstDayProps = {
  name?: string;
  position:
    | NonNullable<CandidateOpportunityOnboarding['positions']>[number]
    | undefined;
  location: string;
  texts: Nullable<
    Pick<
      NonNullable<ReturnType<UseOnboardingTextReturn['getOnboardingTexts']>>,
      'setUpSuccess' | 'startedWithAxiom'
    >
  >;
};

export const EngagementDetailsFirstDay = ({
  name,
  position,
  location,
  texts,
}: OnboardingFirstDayProps) => {
  const { isMobile } = useBreakpoint();
  const candidateOpportunity = position?.candidateOpportunities?.[0];
  const contacts = [
    candidateOpportunity?.onboardingDetails?.contact ?? null,
    ...(candidateOpportunity?.onboardingDetails?.additionalContacts ?? []),
  ].filter(
    (contact): contact is NonNullable<typeof contact> => contact !== null
  );

  return (
    <Well background="blue" name={name}>
      <Layout stackableOn="mobile">
        <LayoutItem rightGutter={isMobile ? '0px' : '16px'}>
          <Visible only="tablet largeScreen">
            <Satchel width="100px" height="100px" />
          </Visible>
        </LayoutItem>
        <LayoutItem fluid>
          <SmallHeader name="FIRST_DAY_HEADER">First Day Details</SmallHeader>
          <Gutter bottom="16px" />
          <Well background="gray">
            {position?.startDate ? (
              <Gutter bottom="8px" name="START_DATE">
                <Layout>
                  <LayoutItem rightGutter="8px">
                    <ParagraphHeader>Start Date:</ParagraphHeader>
                  </LayoutItem>
                  <LayoutItem>
                    <Paragraph>
                      {DateUtil.displayDateLongYear(position.startDate)}
                    </Paragraph>
                  </LayoutItem>
                </Layout>
              </Gutter>
            ) : null}
            <EngagementDetailsFirstDayContacts contacts={contacts} />
            {position?.worksite ? (
              <Gutter bottom="8px">
                <Layout name="WORKSITE">
                  <LayoutItem rightGutter="8px">
                    <ParagraphHeader>Onsite/Remote:</ParagraphHeader>
                  </LayoutItem>
                  <LayoutItem>
                    <Paragraph>{position.worksite}</Paragraph>
                  </LayoutItem>
                </Layout>
              </Gutter>
            ) : null}
            {location ? (
              <Layout name="ADDRESS">
                <LayoutItem rightGutter="8px">
                  <ParagraphHeader>Address:</ParagraphHeader>
                </LayoutItem>
                <LayoutItem>
                  <Paragraph>{location}</Paragraph>
                </LayoutItem>
              </Layout>
            ) : null}
          </Well>
          <Gutter bottom="16px" />
          {candidateOpportunity?.onboardingDetails?.notes ? (
            <Well background="gray">
              <ParagraphHeader>Note from Axiom:</ParagraphHeader>
              <Paragraph>
                <ReadonlyTextarea
                  name="NOTE"
                  value={candidateOpportunity.onboardingDetails.notes}
                  formatting="modern"
                />
              </Paragraph>
            </Well>
          ) : null}
          {texts.setUpSuccess || texts.startedWithAxiom ? (
            <>
              <Gutter bottom="16px" />
              <Well background="gray">
                {texts.setUpSuccess ? (
                  <>
                    <ParagraphHeader>Get Set Up for Success:</ParagraphHeader>
                    <Paragraph>
                      <ReadonlyTextarea
                        name="SET_UP_SUCCESS"
                        value={texts.setUpSuccess}
                        formatting="modern"
                      />
                    </Paragraph>
                  </>
                ) : null}
                {texts.startedWithAxiom ? (
                  <>
                    <ParagraphHeader>
                      Getting Started With Axiom:
                    </ParagraphHeader>
                    <Paragraph>
                      <ReadonlyTextarea
                        name="STARTING_WITH_AXIOM"
                        value={texts.startedWithAxiom}
                        formatting="modern"
                      />
                    </Paragraph>
                  </>
                ) : null}
              </Well>
            </>
          ) : null}
        </LayoutItem>
      </Layout>
    </Well>
  );
};
